import { useMemo } from "react";

import {
  AnalyticsResourceV2Type,
  AnalyzeExperienceFrontendTinybirdRequestBodyType,
  AnalyzeTinybirdResourceResponseBodyType,
  DatasetId,
  ExperienceDto,
  ExperienceFe,
} from "@intelligems/ig-types";
import {
  TinybirdExperimentAnalysisInput,
  TransformedDataset,
} from "@src/api/analytics/types";
import { useBuildTinybirdPayload } from "@src/api/analytics/use-build-tinybird-payload";
import { getMaxZScore, transformMetricsConfig } from "@src/api/analytics/utils";
import { api } from "@src/api/api";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { dashboardKeys } from "@src/api/queryKeys";
import { useSelector } from "@src/store";
import { selectedDashboardFilters } from "@src/store/slices/dashboardFilters";
import { QueryMeta } from "@src/types/react-query";
import { useQueries, useQuery, UseQueryResult } from "@tanstack/react-query";

const fetchExperimentTinybirdAnalytics = async (
  data: AnalyzeExperienceFrontendTinybirdRequestBodyType
) => {
  const resp = await api.post<
    AnalyzeTinybirdResourceResponseBodyType,
    AnalyzeExperienceFrontendTinybirdRequestBodyType
  >(`/v2/analytics/analyze-resource-tinybird`, data);
  return resp.data;
};

export const useGetTinybirdExperienceAnalysis = (
  params: TinybirdExperimentAnalysisInput,
  resourceType: AnalyticsResourceV2Type
): UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[] => {
  const buildTinybirdPayload = useBuildTinybirdPayload();
  const dashboardFilters = useSelector(selectedDashboardFilters);
  const transformedDataSets: TransformedDataset[] = transformMetricsConfig(
    params.graphConfigs
  );

  const result = useQueries({
    queries: transformedDataSets.map((datasetGroup) => {
      const data = buildTinybirdPayload.build(
        params,
        datasetGroup,
        resourceType
      );
      return {
        queryKey: [dashboardKeys.selectedTinybirdExperiment(data)],
        meta: {
          onError: {
            "503": "ignore",
          },
        } as QueryMeta,
        queryFn: async () => fetchExperimentTinybirdAnalytics(data),
        enabled:
          params.enabled !== false &&
          params.experience &&
          !!data &&
          dashboardFilters.hiddenEndTime !== null &&
          params.overviewDataLoaded, // dashboardFilters.hiddenEndTime !== null,
      };
    }),
  });
  // React-queries useQuery returns a new object always, so if you pass
  // this object down the component hierarchy you won't be able to prevent
  // renders. So if we want to pass the whole object we need to be sure
  // the object doesn't change if it's key data components don't change
  return useMemo(
    () => result,
    // eslint-disable-next-line react-compiler/react-compiler
    [
      ...result.flatMap((query) => [
        query.data,
        query.isSuccess,
        query.isFetching,
        query.isError,
        query.isPending,
      ]),
    ]
  );
};
